import React, { useState } from 'react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const PORTAL_SECTIONS = [
  'KPI Cards',
  'Metrics',
  'Capital',
  'Ownership',
  'SPCC',
  'Roadmap',
  'Financials - P&L',
  'Financials - Balance Sheet',
  'Financials - Cash Flow',
  'Headcount',
  'Pipeline',
  'Risks',
  'Next Raise',
  'Documents',
  'Alerts',
];

export default function PortalSettings() {
  const t = useTheme();
  const savedPortal = JSON.parse(localStorage.getItem('finkloz_portal_settings') || 'null');
  const [portalEnabled, setPortalEnabled] = useState(savedPortal?.portalEnabled ?? true);
  const [branding, setBranding] = useState(savedPortal?.branding || {
    accentColor: '#1D9E75',
    welcomeMessage: 'Welcome to the DemoMed investor portal. Here you will find up-to-date financial information and key metrics.',
    footerDisclaimer: 'This information is confidential and intended solely for the named recipient. The financial data presented has not been audited.',
  });
  const [sectionVisibility, setSectionVisibility] = useState(
    savedPortal?.sectionVisibility || Object.fromEntries(PORTAL_SECTIONS.map(s => [s, true]))
  );
  const [permissions, setPermissions] = useState(savedPortal?.permissions || {
    canExportPdf: true,
    canSeeOtherInvestors: false,
  });
  const [sharedLinks, setSharedLinks] = useState(savedPortal?.sharedLinks || {
    defaultExpiration: '30',
    requirePassword: false,
  });
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    localStorage.setItem('finkloz_portal_settings', JSON.stringify({
      portalEnabled, branding, sectionVisibility, permissions, sharedLinks,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ maxWidth: 700 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Investor Portal
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Configure the investor-facing portal, branding, and access controls.
      </p>

      {/* Global Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24,
        padding: 16, background: t.bgSurface, borderRadius: 8, border: '1px solid ' + t.border }}>
        <div onClick={() => { setPortalEnabled(!portalEnabled); markDirty(); }} style={toggleStyle(portalEnabled, t)}>
          <div style={toggleKnob(portalEnabled)} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>
            Investor Portal {portalEnabled ? 'Enabled' : 'Disabled'}
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans }}>
            {portalEnabled ? 'The portal is accessible to investors with valid credentials.' : 'The portal is currently offline.'}
          </div>
        </div>
      </div>

      {/* Branding */}
      <div style={sectionTitleStyle(t)}>Branding</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Accent Color</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="color"
            value={branding.accentColor}
            onChange={e => { setBranding(p => ({ ...p, accentColor: e.target.value })); markDirty(); }}
            style={{ width: 36, height: 36, border: '1px solid ' + t.border, borderRadius: 6, cursor: 'pointer', padding: 2 }}
          />
          <input
            type="text"
            value={branding.accentColor}
            onChange={e => { setBranding(p => ({ ...p, accentColor: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 120, fontFamily: mono }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Welcome Message</label>
        <textarea
          value={branding.welcomeMessage}
          onChange={e => { setBranding(p => ({ ...p, welcomeMessage: e.target.value })); markDirty(); }}
          rows={3}
          style={{ ...inputStyle(t), resize: 'vertical' }}
        />
      </div>

      <div style={{ marginBottom: 32 }}>
        <label style={labelStyle(t)}>Footer Disclaimer</label>
        <textarea
          value={branding.footerDisclaimer}
          onChange={e => { setBranding(p => ({ ...p, footerDisclaimer: e.target.value })); markDirty(); }}
          rows={3}
          style={{ ...inputStyle(t), resize: 'vertical' }}
        />
      </div>

      {/* Section Visibility */}
      <div style={sectionTitleStyle(t)}>Section Visibility</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 32 }}>
        {PORTAL_SECTIONS.map(sec => (
          <div key={sec} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              onClick={() => {
                setSectionVisibility(prev => ({ ...prev, [sec]: !prev[sec] }));
                markDirty();
              }}
              style={toggleStyle(sectionVisibility[sec], t)}
            >
              <div style={toggleKnob(sectionVisibility[sec])} />
            </div>
            <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>{sec}</span>
          </div>
        ))}
      </div>

      {/* Permissions */}
      <div style={sectionTitleStyle(t)}>Permissions</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => { setPermissions(p => ({ ...p, canExportPdf: !p.canExportPdf })); markDirty(); }}
            style={toggleStyle(permissions.canExportPdf, t)}
          >
            <div style={toggleKnob(permissions.canExportPdf)} />
          </div>
          <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>Investors can export PDF</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => { setPermissions(p => ({ ...p, canSeeOtherInvestors: !p.canSeeOtherInvestors })); markDirty(); }}
            style={toggleStyle(permissions.canSeeOtherInvestors, t)}
          >
            <div style={toggleKnob(permissions.canSeeOtherInvestors)} />
          </div>
          <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>Investors can see other investor names</span>
        </div>
      </div>

      {/* Shared Links */}
      <div style={sectionTitleStyle(t)}>Shared Links</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
        <div>
          <label style={labelStyle(t)}>Default Expiration</label>
          <select
            value={sharedLinks.defaultExpiration}
            onChange={e => { setSharedLinks(p => ({ ...p, defaultExpiration: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
          >
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              onClick={() => { setSharedLinks(p => ({ ...p, requirePassword: !p.requirePassword })); markDirty(); }}
              style={toggleStyle(sharedLinks.requirePassword, t)}
            >
              <div style={toggleKnob(sharedLinks.requirePassword)} />
            </div>
            <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>Require password</span>
          </div>
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
